import { useEffect, useRef, useState } from 'react';

import Button from '../../generic/Button';
import LabeledPasswordInput from '../../generic/Forms/LabeledPasswordInput';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';
import { icons } from '../../generic/Icon';

import styles from '../Login.module.less';

export type ConfirmPasswordStepProps = {
  /** The returning email whose password is being confirmed, shown read-only. */
  email: string;
  isSubmitting: boolean;
  /** Inline error from a failed confirmation (e.g. an incorrect password). */
  error: string | null;
  /**
   * Whether this deployment can mail a verification code (`LoginStateIdle.emailVerificationAvailable`).
   * When false — on-prem, or the `EMAIL_VERIFICATION` flag off — the emailed-code alternative is hidden
   * rather than offered and then refused by the server; the password remains the only proof.
   */
  canConfirmViaEmail: boolean;
  onSubmit: (password: string) => void;
  /** Prove ownership with an emailed code instead of the password (e.g. it's forgotten). */
  onConfirmViaEmail: () => void;
  onBack: () => void;
};

/**
 * Create-new identity proof for a returning email that already has a password in another org: they re-enter it to
 * prove control before a new org-less identity is provisioned — or, where codes can be mailed, choose the
 * emailed-code proof instead. No strength meter — the password is an existing one, verified server-side against a
 * sibling. (The all-SSO variant, which has no password to confirm, uses the set-a-password step plus email
 * verification instead.)
 */
const ConfirmPasswordStep = (
  { email, isSubmitting, error, canConfirmViaEmail, onSubmit, onConfirmViaEmail, onBack }: ConfirmPasswordStepProps,
) => {
  const [password, setPassword] = useState('');
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  const submit = () => {
    if (!password || isSubmitting) {
      return;
    }
    onSubmit(password);
  };

  return (
    <form
      className={styles.card}
      onSubmit={event => {
        event.preventDefault();
        submit();
      }}
    >
      <h1 className={styles.heading}>Create a new organization</h1>
      <p className={styles.placeholder}>
        Confirm your password to continue as {email}.
        {canConfirmViaEmail && (
          <Button
            type="anchor"
            size="small"
            isDisabled={isSubmitting}
            onClick={onConfirmViaEmail}
          >
            Email me a confirmation code instead
          </Button>
        )}
      </p>
      <div className={styles.fieldGroup}>
        <LabeledTextInput label="Email" name="email" value={email} isReadOnly />
        <LabeledPasswordInput ref={passwordRef} label="Password" value={password} onChange={setPassword} isRequired />
      </div>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      {/* A submit button lets Enter submit the form; the confirm runs in the form's onSubmit, so onClick is unused. */}
      <Button isFullWidth size="large" isSubmitButton isDisabled={!password || isSubmitting} onClick={() => undefined}>
        Continue
      </Button>
      <Button
        type="anchor"
        size="small"
        icon={icons.arrowLeft}
        className={styles.backLink}
        isDisabled={isSubmitting}
        onClick={onBack}
      >
        Back
      </Button>
    </form>
  );
};

export default ConfirmPasswordStep;
